/**
 * <MentionInput> — the Assistant composer, with `@`.
 *
 * Typing `@` opens a picker over the app's people, its roles, and any master
 * it has declared semantically searchable — suppliers, items, customers,
 * documents. Which objects are mentionable needs no new setting: an object is
 * mentionable exactly when its model already declares `vector_search`, the
 * same flag that makes it findable by meaning everywhere else.
 *
 * Picking inserts a **typed token** — `@[user:ravi]`, `@[ITEM:SKU-4471]` —
 * rather than the name it was displayed as. That is the whole point of the
 * feature: the model receives a resolved identifier instead of a word to guess
 * at, which is the difference between "ask Ravi to order 10 XYZ" reliably
 * meaning the right Ravi and the right SKU, and usually meaning them.
 *
 * Shared by both Assistant surfaces. Without a `search` function it is an
 * ordinary text input, so a host that serves no mention endpoint loses the
 * picker and nothing else.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties, KeyboardEvent } from 'react'

/** One thing an `@` can name. */
export interface MentionHit {
  /** What is inserted: `@[user:ravi]`, `@[ITEM:SKU-4471]`. */
  token: string
  /** What the picker shows. */
  label: string
  /** The second line — an email, the roles held, what the record is. */
  detail?: string
  id?: string
  obj_name?: string
}

export interface MentionGroups {
  people?: MentionHit[]
  roles?: MentionHit[]
  records?: MentionHit[]
}

export interface MentionInputProps {
  value: string
  onChange: (next: string) => void
  placeholder?: string
  style?: CSSProperties
  disabled?: boolean
  /** Absent → a plain input, no picker. */
  search?: (query: string) => Promise<MentionGroups>
  /** Enter with no picker open. */
  onSubmit?: () => void
  /** Debounce for the lookup; the picker is typed into fast. */
  debounceMs?: number
}

/** The `@word` immediately before the caret, or null. */
function activeMention(text: string, caret: number): { term: string; at: number } | null {
  const upto = text.slice(0, caret)
  const at = upto.lastIndexOf('@')
  if (at < 0) return null
  // Only at a word boundary: an email address is not a mention.
  if (at > 0 && !/\s/.test(upto[at - 1])) return null
  const term = upto.slice(at + 1)
  // A completed token is done being edited, and a space ends a mention: the
  // picker must not reopen over the sentence that follows it.
  if (term.includes(']') || /\s/.test(term)) return null
  if (term.length > 40) return null
  return { term, at }
}

function flatten(groups: MentionGroups): Array<MentionHit & { group: string }> {
  return [
    ...(groups.people || []).map((h) => ({ ...h, group: 'People' })),
    ...(groups.roles || []).map((h) => ({ ...h, group: 'Roles' })),
    ...(groups.records || []).map((h) => ({ ...h, group: 'Records' })),
  ]
}

export default function MentionInput({
  value, onChange, placeholder, style, disabled, search, onSubmit,
  debounceMs = 180,
}: MentionInputProps) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [hits, setHits] = useState<Array<MentionHit & { group: string }>>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const mention = useRef<{ term: string; at: number } | null>(null)
  const alive = useRef(true)

  useEffect(() => () => { alive.current = false }, [])

  const lookup = useCallback((term: string) => {
    if (!search) return
    void (async () => {
      try {
        const res = await search(term)
        if (!alive.current) return
        const rows = flatten(res || {})
        setHits(rows)
        setActive(0)
        setOpen(rows.length > 0)
      } catch {
        // A picker that cannot load is simply not offered; the person is
        // typing a message, not filing a bug.
        if (alive.current) { setHits([]); setOpen(false) }
      }
    })()
  }, [search])

  // Debounced: the picker is typed into a character at a time.
  useEffect(() => {
    if (!mention.current || !search) return undefined
    const term = mention.current.term
    const id = window.setTimeout(() => lookup(term), debounceMs)
    return () => window.clearTimeout(id)
  }, [value, lookup, search, debounceMs])

  const handleChange = useCallback((next: string, caret: number) => {
    onChange(next)
    const found = search ? activeMention(next, caret) : null
    mention.current = found
    if (!found) { setOpen(false); setHits([]) }
  }, [onChange, search])

  const pick = useCallback((hit: MentionHit) => {
    const el = inputRef.current
    const m = mention.current
    if (!el || !m) return
    const caret = el.selectionStart ?? value.length
    const next = `${value.slice(0, m.at)}${hit.token} ${value.slice(caret)}`
    onChange(next)
    mention.current = null
    setOpen(false)
    setHits([])
    // Put the caret after the token we just inserted, so typing continues
    // where the person was rather than at the end of what they had written.
    const pos = m.at + hit.token.length + 1
    window.requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(pos, pos)
    })
  }, [value, onChange])

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (open && hits.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault(); setActive((i) => (i + 1) % hits.length); return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault(); setActive((i) => (i - 1 + hits.length) % hits.length); return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        // Enter picks the highlighted mention rather than sending: the person
        // is mid-word, and sending half a sentence is not what they meant.
        e.preventDefault(); pick(hits[active]); return
      }
      if (e.key === 'Escape') { e.preventDefault(); setOpen(false); return }
    }
    if (e.key === 'Enter' && onSubmit) { e.preventDefault(); onSubmit() }
  }, [open, hits, active, pick, onSubmit])

  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      {open && hits.length > 0 && (
        <div
          role="listbox"
          style={{
            position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, right: 0,
            maxHeight: 240, overflowY: 'auto', zIndex: 40,
            borderRadius: 10, background: 'var(--color-bg, #fff)',
            border: '1px solid var(--color-border, #e2e8f0)',
            boxShadow: '0 12px 32px rgba(15,23,42,0.16)',
          }}
        >
          {hits.map((h, i) => {
            const first = i === 0 || hits[i - 1].group !== h.group
            return (
              <div key={`${h.token}-${i}`}>
                {first && (
                  <div style={{
                    padding: '5px 10px 3px', fontSize: '0.8125rem', fontWeight: 700,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    color: 'var(--color-text-secondary, #94a3b8)',
                  }}>{h.group}</div>
                )}
                <div
                  role="option"
                  aria-selected={i === active}
                  onMouseDown={(e) => { e.preventDefault(); pick(h) }}
                  onMouseEnter={() => setActive(i)}
                  style={{
                    padding: '0.4rem 0.65rem', cursor: 'pointer', fontSize: '0.8825rem',
                    background: i === active
                      ? 'var(--color-accent-weak, rgba(76,141,255,0.12))'
                      : 'transparent',
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{h.label}</div>
                  {h.detail && (
                    <div style={{
                      fontSize: '0.8125rem',
                      color: 'var(--color-text-secondary, #64748b)',
                    }}>{h.detail}</div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
      <input
        ref={inputRef}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => handleChange(e.currentTarget.value,
                                      e.currentTarget.selectionStart ?? e.currentTarget.value.length)}
        onKeyDown={onKeyDown}
        onBlur={() => window.setTimeout(() => { if (alive.current) setOpen(false) }, 120)}
        style={{ width: '100%', ...style }}
      />
    </div>
  )
}
